import { Routes } from '@angular/router';

import { LoginComponent } from './login/login/login.component';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { authGuard } from './core/auth/auth.guard';
import { loginGuard } from './core/auth/login.guard';
import { moduleGuard } from './core/auth/module.guard';
import { OrganizationModuleEnum } from './core/models/organization-module.enum';

export const routes: Routes = [
    { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    { path: 'login', component: LoginComponent, canActivate: [loginGuard] },
    { 
        path: 'register', 
        loadComponent: () => import('./register/register.component').then(m => m.RegisterComponent), 
        canActivate: [loginGuard] 
    },
    { 
        path: 'forgot-password', 
        loadComponent: () => import('./login/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent), 
        canActivate: [loginGuard] 
    },
    { 
        path: 'password-reset', 
        loadComponent: () => import('./login/reset-password/reset-password.component').then(m => m.ResetPasswordComponent), 
        canActivate: [loginGuard] 
    },
    { 
        path: 'account-confirm', 
        loadComponent: () => import('./login/account-confirm/account-confirm.component').then(m => m.AccountConfirmComponent), 
        canActivate: [loginGuard] 
    },
    {
        path: '',
        component: MainLayoutComponent,
        canActivate: [authGuard],
        children: [
            {
                path: 'dashboard',
                loadComponent: () => import('./dashboard/dashboard.page.component').then(m => m.DashboardPageComponent)
            },
            { 
                path: 'users', 
                loadComponent: () => import('./users/users.component').then(m => m.UsersComponent) 
            },
            { 
                path: 'users/:id', 
                loadComponent: () => import('./users/pages/user-detail/user-detail.page.component').then(m => m.UserDetailPageComponent) 
            },
            { 
                path: 'organization', 
                loadComponent: () => import('./organization/organization').then(m => m.OrganizationComponent) 
            },
            { 
                path: 'organizations', 
                loadComponent: () => import('./admin/organizations/organizations.component').then(m => m.OrganizationsComponent) 
            },
            { 
                path: 'organizations/:id', 
                loadComponent: () => import('./admin/organizations/pages/organization-detail/organization-detail.page.component').then(m => m.OrganizationDetailPageComponent) 
            },
            { 
                path: 'admin/speech-analysis-feedback', 
                loadComponent: () => import('./admin/speech-analysis-feedback/speech-analysis-feedback.page.component').then(m => m.SpeechAnalysisFeedbackPageComponent) 
            },
            {
                path: 'registration-links',
                loadComponent: () => import('./registration-links/registration-links.component').then(m => m.RegistrationLinksComponent)
            },
            {
                path: 'teams',
                loadComponent: () => import('./organization-teams/organization-teams.component').then(m => m.OrganizationTeamsComponent)
            },
            {
                path: 'teams/:id',
                loadComponent: () => import('./organization-teams/pages/team-detail/team-detail.page.component').then(m => m.TeamDetailPageComponent)
            },
            { 
                path: 'speech-analysis-categories', 
                loadComponent: () => import('./speech-analysis-categories/speech-analysis-categories.component').then(m => m.SpeechAnalysisCategoriesComponent), 
                canActivate: [moduleGuard], 
                data: { module: OrganizationModuleEnum.SPEECH_ANALYSIS } 
            },
            { 
                path: 'speech-analysis-templates', 
                loadComponent: () => import('./speech-analysis-templates/speech-analysis-templates.component').then(m => m.SpeechAnalysisTemplatesComponent), 
                canActivate: [moduleGuard], 
                data: { module: OrganizationModuleEnum.SPEECH_ANALYSIS } 
            },
            { 
                path: 'speech-analysis/new', 
                loadComponent: () => import('./speech-analysis/new-speech-analysis/new-speech-analysis.component').then(m => m.NewSpeechAnalysisComponent), 
                canActivate: [moduleGuard], 
                data: { module: OrganizationModuleEnum.SPEECH_ANALYSIS } 
            },
            { 
                path: 'speech-analysis', 
                loadComponent: () => import('./speech-analysis/speech-analysis.component').then(m => m.SpeechAnalysisComponent), 
                canActivate: [moduleGuard], 
                data: { module: OrganizationModuleEnum.SPEECH_ANALYSIS } 
            },
            { 
                path: 'content-documents', 
                loadComponent: () => import('./content-documents/content-documents.page.component').then(m => m.ContentDocumentsPageComponent), 
                canActivate: [moduleGuard], 
                data: { module: OrganizationModuleEnum.SPEECH_ANALYSIS } 
            },
            { 
                path: 'content-documents/:id', 
                loadComponent: () => import('./content-documents/content-document-detail/content-document-detail.page.component').then(m => m.ContentDocumentDetailPageComponent), 
                canActivate: [moduleGuard], 
                data: { module: OrganizationModuleEnum.SPEECH_ANALYSIS } 
            },
            { 
                path: 'administration/documents', 
                loadComponent: () => import('./organization-documents/organization-documents.page.component').then(m => m.OrganizationDocumentsPageComponent) 
            },
            { 
                path: 'speech-scripts', 
                loadComponent: () => import('./speech-script/pages/user-pitches/speech-scripts.page.component').then(m => m.SpeechScriptsPageComponent), 
                canActivate: [moduleGuard], 
                data: { module: OrganizationModuleEnum.SPEECH_ANALYSIS } 
            },
            { 
                path: 'speech-scripts/:id', 
                loadComponent: () => import('./speech-script/pages/speech-script-detail/speech-script-detail.page.component').then(m => m.SpeechScriptDetailPageComponent), 
                canActivate: [moduleGuard], 
                data: { module: OrganizationModuleEnum.SPEECH_ANALYSIS } 
            },
            {
                path: 'profile',
                loadComponent: () => import('./user-profile/user-profile.page.component').then(m => m.UserProfilePageComponent)
            },
            {
                path: 'feedbacks',
                loadComponent: () => import('./feedback-general/pages/feedback-general.page.component').then(m => m.FeedbackGeneralPageComponent)
            },
            {
                path: 'feedbacks/:id',
                loadComponent: () => import('./feedback-general/pages/feedback-details/feedback-general-detail.page.component').then(m => m.FeedbackGeneralDetailPageComponent)
            },
            {
                path: 'notifications',
                loadComponent: () => import('./notifications/pages/notifications/notifications.page.component').then(m => m.NotificationsPageComponent)
            },
            {
                path: 'conversation-analysis',
                loadComponent: () => import('./conversation-analysis/conversation-analysis.component').then(m => m.ConversationAnalysisComponent),
                canActivate: [moduleGuard],
                data: { module: OrganizationModuleEnum.CONVERSATION_ANALYSIS }
            },
            {
                path: 'conversation-analysis/new',
                loadComponent: () => import('./conversation-analysis/new-conversation-analysis/new-conversation-analysis.component').then(m => m.NewConversationAnalysisComponent),
                canActivate: [moduleGuard],
                data: { module: OrganizationModuleEnum.CONVERSATION_ANALYSIS }
            },
            {
                path: 'conversation-templates',
                loadComponent: () => import('./conversation-templates/pages/conversation-templates-overview/conversation-templates-overview.page.component').then(m => m.ConversationTemplatesOverviewPageComponent),
                canActivate: [moduleGuard],
                data: { module: OrganizationModuleEnum.CONVERSATION_ANALYSIS }
            },
            {
                path: 'conversation-templates/:id',
                loadComponent: () => import('./conversation-templates/pages/conversation-template-detail/conversation-template-detail.page.component').then(m => m.ConversationTemplateDetailPageComponent),
                canActivate: [moduleGuard],
                data: { module: OrganizationModuleEnum.CONVERSATION_ANALYSIS }
            },
            {
                path: 'ai-usage',
                loadComponent: () => import('./ai-usage/ai-usage.page.component').then(m => m.AiUsagePageComponent)
            },
            {
                path: 'conversation-exports',
                loadComponent: () => import('./conversation-exports/conversation-exports.page.component').then(m => m.ConversationExportsPageComponent)
            },
            {
                path: 'ai-models',
                loadComponent: () => import('./ai-models/ai-models.page.component').then(m => m.AiModelsPageComponent)
            },
            {
                path: 'ai-settings',
                loadComponent: () => import('./admin/ai-settings/ai-settings.page.component').then(m => m.AiSettingsPageComponent)
            }
        ]
    }
];
